import './Overlay.scss';
import React from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { overlayActions } from 'store/slices/overlay';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @description Props for the Overlay component
 * @interface OverlayProps
 * @property {string} [path] - Optional path to navigate to when the overlay is clicked
 */
type OverlayProps = {
  path?: string;
};

/**
 * @description Overlay component that covers the screen and can navigate to a specified path when clicked.
 * Gets the navigation path from Redux state and dispatches a close action when clicked.
 *
 * @SADtrace SAD-003-4.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @component
 * @param {OverlayProps} props - The component props
 *
 * @returns {JSX.Element} The rendered Overlay component
 */
const Overlay: FC<OverlayProps> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const path = useSelector((state: any) => state.overlay.path);

  const overlayClass = 'overlay';

  /**
   * @description Handles click events on the overlay.
   * If a path exists and the click target is the overlay itself, closes the overlay and navigates to the specified path.
   *
   * @SADtrace SAD-003-4.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {React.MouseEvent<HTMLDivElement, MouseEvent>} e - The click event
   *
   * @returns {void}
   */
  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!!path && e.currentTarget.className === overlayClass) {
      dispatch(overlayActions.close());
      navigate(path);
    }
  };

  return (
    <div
      className={overlayClass}
      onClick={handleClick}
      data-testid="overlay"
    ></div>
  );
};

export default Overlay;
